import { createPortal } from 'react-dom';
import Hero from '../../features/auth/components/Hero';
import StorePortal from '../../features/auth/pages/StorePortal';
import StoreRegistrationOnboardingPage from '../../features/store-registration/pages/StoreRegistrationOnboardingPage';
import StoreDashboard from '../../features/queue-management/pages';
import UserDashboard from '../../features/analytics/components/UserDashboard';
import ProductDetail from '../../features/offers/components/ProductDetail';
import Pricing from '../../features/settings/pages/Pricing';
import Blog from '../../features/settings/pages/Blog';
import Support from '../../features/settings/pages/Support';
import ComingSoon from '../../shared/components/ui/ComingSoon';
import Breadcrumb from '../../shared/components/ui/Breadcrumb';
import NotFound from '../../features/errors/NotFound';
import Sidebar from '../../shared/components/layout/Sidebar';
import { env } from '../../config/env';
import {
  PRODUCT_DETAIL_TYPES,
  isPlaceholderRoute
} from './routes';
import {
  StoreBilling,
  StoreProfile,
  StoreSettings,
  StoreUpgradePlan
} from '../../features/account';
import { LegalDocumentPage } from '../../features/legal';
import './RouteRenderer.css';

const PLACEHOLDER_TITLES = {
  '/docs': 'Documentation',
  '/changelog': 'Changelog',
  '/community': 'Community'
};

const SyncingDataLoader = () => (
  <div className="global-request-overlay" aria-live="polite" aria-label="Syncing data">
    <div className="global-request-loader">
      <span className="global-request-spinner" aria-hidden="true" />
      <p>Syncing data...</p>
    </div>
  </div>
);

const RouteBootLoader = () => <SyncingDataLoader />;

const OverlayPortal = ({ children }) => {
  if (typeof document === 'undefined') {
    return children;
  }
  return createPortal(children, document.body);
};

const RouteRenderer = ({
  route,
  authRole,
  authToken,
  storeRegister,
  storeLogin,
  storeToken,
  storeUuid,
  storedStoreUuid,
  storedToken,
  isStoreLoggedIn,
  request,
  onPushActivity,
  onToast,
  onNavigate,
  onStoreRegisterFieldChange,
  onStoreLoginFieldChange,
  onStoreRegister,
  onStoreLogin,
  onGoogleAuthSuccess,
  onStoreLogout,
  showSidebar,
  membershipEnabled,
  shouldShowNotFound,
  shouldShowRouteBootLoader,
  isInitialBootLoading,
  hasResolvedRenderableRoute
}) => {
  const isQueueConsoleRoute = route.startsWith('/store/manage');
  const pageInnerClass = `page-inner${showSidebar ? ' wide' : isQueueConsoleRoute ? ' full' : ''}`;
  const shouldShowBreadcrumbs = false;
  const contentClassName = showSidebar ? 'main-content' : 'route-content';

  const renderResolvedRoute = () => {
    const productType = PRODUCT_DETAIL_TYPES.find((type) => route === `/${type}`);
    if (productType) {
      return <ProductDetail type={productType} />;
    }

    if (authRole === 'store' && (route === '/store' || route.startsWith('/store/manage') || route.startsWith('/store/memberships')) && !shouldShowNotFound) {
      return (
        <StoreDashboard
          request={request}
          pushActivity={onPushActivity}
          onToast={onToast}
          routePath={route}
          membershipEnabled={membershipEnabled}
        />
      );
    }

    switch (route) {
      case '/':
        return (
          <Hero
            onStoreLogin={() => {
              if (storedToken) {
                onToast({ message: 'Session is still active, logging in', variant: 'info' });
                onNavigate('/store');
              } else {
                onNavigate('/login');
              }
            }}
            onStoreRegister={() => {
              onNavigate('/register');
            }}
          />
        );
      case '/pricing':
      case '/pricing/enterprise':
      case '/pricing/users':
        return <Pricing />;
      case '/blog':
        return <Blog />;
      case '/support':
        return <Support />;
      case '/docs':
      case '/changelog':
      case '/community':
        return <ComingSoon title={PLACEHOLDER_TITLES[route]} />;
      case '/login':
        return (
          <StorePortal
            storeRegister={storeRegister}
            storeLogin={storeLogin}
            onStoreRegisterFieldChange={onStoreRegisterFieldChange}
            onStoreLoginFieldChange={onStoreLoginFieldChange}
            onRegister={onStoreRegister}
            onLogin={onStoreLogin}
            onGoogleAuthSuccess={onGoogleAuthSuccess}
            request={request}
            authToken={authToken}
            onToast={onToast}
            mode="login"
          />
        );
      case '/register':
        return env.storeRegistrationOnboardingV2Enabled
          ? (
            <StoreRegistrationOnboardingPage
              route={route}
              onNavigate={onNavigate}
              request={request}
              onToast={onToast}
            />
          )
          : (
            <StorePortal
              storeRegister={storeRegister}
              storeLogin={storeLogin}
              onStoreRegisterFieldChange={onStoreRegisterFieldChange}
              onStoreLoginFieldChange={onStoreLoginFieldChange}
              onRegister={onStoreRegister}
              onLogin={onStoreLogin}
              onGoogleAuthSuccess={onGoogleAuthSuccess}
              request={request}
              authToken={authToken}
              onToast={onToast}
              mode="register"
            />
          );
      case '/store/upgrade':
        return authRole === 'store' && isStoreLoggedIn ? <StoreUpgradePlan /> : null;
      case '/store/profile':
        return authRole === 'store' && isStoreLoggedIn
          ? (
            <StoreProfile
              request={request}
              storeToken={storeToken}
              storeUuid={storedStoreUuid || storeUuid}
              onToast={onToast}
              onStoreLogout={onStoreLogout}
            />
          )
          : null;
      case '/store/billing':
        return authRole === 'store' && isStoreLoggedIn ? <StoreBilling /> : null;
      case '/store/settings':
        return authRole === 'store' && isStoreLoggedIn
          ? (
            <StoreSettings
              request={request}
              storeToken={storeToken}
              storeUuid={storedStoreUuid || storeUuid}
              onToast={onToast}
              onStoreLogout={onStoreLogout}
            />
          )
          : null;
      case '/store/privacy':
        return authRole === 'store' && isStoreLoggedIn ? <LegalDocumentPage docKey="privacy" /> : null;
      case '/store/terms':
        return authRole === 'store' && isStoreLoggedIn ? <LegalDocumentPage docKey="terms" /> : null;
      case '/store/refund':
        return authRole === 'store' && isStoreLoggedIn ? <LegalDocumentPage docKey="refund" /> : null;
      case '/store/shipping':
        return authRole === 'store' && isStoreLoggedIn ? <LegalDocumentPage docKey="shipping" /> : null;
      case '/store/contact':
        return authRole === 'store' && isStoreLoggedIn ? <LegalDocumentPage docKey="contact" /> : null;
      case '/user':
        return authRole !== 'store'
          ? (
            <UserDashboard
              request={request}
              pushActivity={onPushActivity}
            />
          )
          : null;
      default:
        if (route.startsWith('/register/')) {
          if (!env.storeRegistrationOnboardingV2Enabled) {
            return (
              <StorePortal
                storeRegister={storeRegister}
                storeLogin={storeLogin}
                onStoreRegisterFieldChange={onStoreRegisterFieldChange}
                onStoreLoginFieldChange={onStoreLoginFieldChange}
                onRegister={onStoreRegister}
                onLogin={onStoreLogin}
                onGoogleAuthSuccess={onGoogleAuthSuccess}
                request={request}
                authToken={authToken}
                onToast={onToast}
                mode="register"
              />
            );
          }
          return (
            <StoreRegistrationOnboardingPage
              route={route}
              onNavigate={onNavigate}
              request={request}
              onToast={onToast}
            />
          );
        }
        return null;
    }
  };

  return (
    <>
      <main className="page route-renderer-page">
        <div className={`${pageInnerClass} content route-renderer-main${showSidebar ? ' with-sidebar' : ''}${isPlaceholderRoute(route) ? ' centered-layout' : ''}`}>
          {showSidebar && (
            <Sidebar currentPath={route} onNavigate={onNavigate} membershipEnabled={membershipEnabled} />
          )}
          <div className={contentClassName}>
            {shouldShowBreadcrumbs ? <Breadcrumb path={route} onNavigate={onNavigate} /> : null}
            {shouldShowRouteBootLoader && (
              <OverlayPortal>
                <RouteBootLoader />
              </OverlayPortal>
            )}
            {shouldShowNotFound && <NotFound />}
            {!shouldShowRouteBootLoader && !shouldShowNotFound && renderResolvedRoute()}
            {!isInitialBootLoading && !hasResolvedRenderableRoute && (
              <OverlayPortal>
                <RouteBootLoader />
              </OverlayPortal>
            )}
          </div>
        </div>
      </main>
      {isInitialBootLoading && (
        <OverlayPortal>
          <SyncingDataLoader />
        </OverlayPortal>
      )}
    </>
  );
};

export default RouteRenderer;
